import { Stack, Box, Button, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { useOutletContext, useNavigate } from 'react-router';
import { ProfileSetupContext } from './ProfileSetupContext';
import {
  BackButton,
  Routes,
  useDeepEqualMemo,
} from '@xolv-mybrightlink/common';

const BOXES = [
  'iep-support',
  'resources-for-high-functioning',
  'respite-care',
  'family-nights-out',
  'occupational-skill-building',
  'child-play-dates',
  'parent-mentorship',
  'puberty-support',
];

const ActionButton = styled(Button)({
  '&.MuiButton-root': {
    mt: 3,
    mb: 4,
    py: 22.5,
    px: 4.5,
  },
  '&.MuiButtonBase-root.Mui-disabled': {
    color: '#fff',
    backgroundColor: '#b1b5bd',
  },
});

export function ProfileSetupInterests() {
  const activeStyle = {
    border: '0.5px solid #d8dade',
    backgroundColor: '#307fe2',
  };

  const activeTextStyle = {
    color: '#fff',
    fontWeight: 500,
  };

  const { profile, updateProfile } = useOutletContext<ProfileSetupContext>();

  const interested = useDeepEqualMemo(new Set(profile?.interests ?? []));

  const updateInterested = useCallback(
    (choiceKey: string) => {
      updateProfile('interests', () => {
        const selected = interested.has(choiceKey);
        const newInterested = new Set(interested);
        if (selected) {
          newInterested.delete(choiceKey);
        } else {
          newInterested.add(choiceKey);
        }
        return Array.from(newInterested);
      });
    },
    [updateProfile, interested],
  );

  const nextStep = interested.size > 0;
  const navigate = useNavigate();

  return (
    <Stack spacing={20}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 600, color: '#3b4559' }}>
          <FormattedMessage id='profile.setup.message-1' />
        </Typography>

        <Typography variant='body1' sx={{ mt: 2.5, color: '#626a7a' }}>
          <FormattedMessage id='profile.setup.message-2' />
        </Typography>

        <Box
          sx={{
            mt: 10,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
          }}
        >
          {BOXES.map((choiceKey: string) => {
            const active = interested.has(choiceKey);
            return (
              <Box
                key={choiceKey}
                sx={{
                  width: 'fit',
                  borderRadius: 22,
                  boxShadow:
                    '0 12px 16px 0 rgba(59, 69, 89, 0.05), 0 8px 10px 0 rgba(59, 69, 89, 0.1)',
                  border: '0.5px solid #d8dade',
                  backgroundColor: '#fff',
                  px: 5,
                  py: 4.5,
                  cursor: 'pointer',
                  ...(active ? activeStyle : {}),
                }}
                onClick={() => updateInterested(choiceKey)}
              >
                <Typography
                  variant='body1'
                  sx={{
                    color: '#3b4559',
                    cursor: 'pointer',
                    userSelect: 'none',
                    ...(active ? activeTextStyle : {}),
                  }}
                >
                  <FormattedMessage id={`profile.interests.${choiceKey}`} />
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          width: 'calc(100vw - 250px)',
          left: '250px' /* drawer width */,
          bottom: 20,
          mt: 20,
        }}
      >
        <Box sx={{ display: 'flex', gap: 8, width: 'fit-content', mx: 'auto' }}>
          <BackButton size='large' />
          <ActionButton
            type='submit'
            variant='contained'
            color='inherit'
            size='large'
            sx={{
              flex: 1,
              backgroundColor: '#f7f8fb',
              color: '#307fe2',
              borderRadius: 22,
              border: '1px solid #307fe2',
              px: 22.5,
            }}
            disableElevation
            disableRipple={true}
            onClick={() => {
              navigate(Routes.profile.behaviors);
            }}
          >
            <FormattedMessage id='actions.skip' />
          </ActionButton>
          <ActionButton
            variant='contained'
            size='large'
            sx={{
              flex: 1,
              borderRadius: 22,
              backgroundColor: '#307fe2',
              px: 35,
              py: 4.5,
            }}
            disableElevation
            disableRipple={true}
            disabled={!nextStep}
            onClick={() => {
              navigate(Routes.profile.behaviors);
            }}
          >
            <FormattedMessage id='common.next' />
          </ActionButton>
        </Box>
      </Box>
    </Stack>
  );
}
